/**
 * Every time the site shows a date or a clock, it goes through here.
 *
 * The discipline is one rule: **store and transport UTC, convert only at the edge.** The API speaks
 * Unix seconds, which is an unambiguous instant with no timezone attached. The browser is the only
 * thing that knows where the reader is, so it is the only thing that decides what day or hour that
 * instant falls in.
 *
 * That also means day grouping belongs on the client. A server grouping matches into days has to
 * pick a timezone, and whichever it picks puts a late-evening match on the wrong date for a large
 * part of the audience.
 */

/**
 * Clock time in the reader's timezone.
 *
 * `hour12: false` is deliberate. Left to the locale, some regions render "2:39 PM" as "02:39" with
 * no meridiem, which reads as 2am and is off by twelve hours — a real bug that shipped here. A
 * 24-hour clock cannot be misread that way, and this is a schedule where the difference between
 * morning and afternoon matters.
 */
export function formatTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/** Full date in the reader's timezone, e.g. "Thursday, 13 August". */
export function formatDay(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

/** A stable key for "which local day is this", used only for grouping. */
export function localDayKey(unixSeconds: number): string {
  const date = new Date(unixSeconds * 1000)
  // Built from local parts rather than toISOString, which would silently convert back to UTC and
  // reintroduce exactly the boundary problem this module exists to avoid.
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** The reader's timezone name, shown once so the times on the page are unambiguous. */
export function timezoneLabel(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'local time'
  }
}

/** "in 25 min" / "2 h ago" — relative phrasing for things near now. */
export function relativeTime(unixSeconds: number): string {
  const deltaSeconds = unixSeconds - Date.now() / 1000
  const absolute = Math.abs(deltaSeconds)

  if (absolute < 90 * 60) {
    const minutes = Math.round(absolute / 60)
    return deltaSeconds > 0 ? `in ${minutes} min` : `${minutes} min ago`
  }

  const hours = Math.round(absolute / 3600)
  if (hours < 36) {
    return deltaSeconds > 0 ? `in ${hours} h` : `${hours} h ago`
  }

  const days = Math.round(absolute / 86_400)
  return deltaSeconds > 0 ? `in ${days} d` : `${days} d ago`
}

/** Group matches into local days, oldest first. */
export function groupByLocalDay<T extends { startTime: number }>(
  items: readonly T[],
): { key: string; startTime: number; items: T[] }[] {
  const groups = new Map<string, { key: string; startTime: number; items: T[] }>()

  for (const item of items) {
    const key = localDayKey(item.startTime)
    const existing = groups.get(key)
    if (existing) {
      existing.items.push(item)
      existing.startTime = Math.min(existing.startTime, item.startTime)
    } else {
      groups.set(key, { key, startTime: item.startTime, items: [item] })
    }
  }

  return [...groups.values()].sort((a, b) => a.startTime - b.startTime)
}

/** Short form for a tab: "Fri 15". Long enough to identify a day, short enough to sit in a row. */
export function formatDayShort(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
  })
}

/** Is this timestamp on the reader's today? Used to label a tab rather than to compute anything. */
export function isToday(unixSeconds: number): boolean {
  return localDayKey(unixSeconds) === localDayKey(Date.now() / 1000)
}
